import React, { useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, UserIcon, CreditCardIcon, FileTextIcon, MessageSquareIcon } from 'lucide-react';
import { Refund, RefundStatus } from '../../../shared/types/Refund';
export function RefundDetail() {
  const {
    t
  } = useTranslation('refunds');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalNotes, setApprovalNotes] = useState('');
  // Mock data - in a real app, this would be fetched from an API
  const isAttentionItem = id === 'RF-2024-0045';
  const refund: Refund = isAttentionItem ? {
    id: 'RF-2024-0045',
    refundId: 'RF-2024-0045',
    transactionId: 'TXN-2026-5512',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: getTenant().currency.code,
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund — order delivered late',
    requestedBy: 'Ahmad Rahman',
    requestedAt: '2026-05-04T09:00:00Z',
    customerName: 'Ahmad Rahman',
    customerEmail: 'ahmad.rahman@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4521',
    merchantName: BRAND.name,
    merchantId: 'MERCH-MY-001',
    notes: 'Flagged from Attention Needed — requires merchant approval within 1 day.',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2026-05-04T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Ahmad Rahman'
    }, {
      id: '2',
      timestamp: '2026-05-04T09:05:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  } : {
    id: id || '1',
    refundId: 'REF-2024-001',
    transactionId: 'TXN-2024-1234',
    originalAmount: 125000,
    refundAmount: 125000,
    currency: getTenant().currency.code,
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested cancellation due to order error',
    requestedBy: 'John Smith',
    requestedAt: '2024-01-18T10:30:00Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4242',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    notes: 'Customer called support to request refund. Order was placed by mistake.',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T10:30:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'John Smith'
    }, {
      id: '2',
      timestamp: '2024-01-18T10:35:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  };
  const getStatusBadge = (status: RefundStatus | string) => {
    const styles: Record<string, string> = {
      requested: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    const labels: Record<string, string> = {
      requested: 'Requested',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      processing: 'Processing',
      completed: 'Completed',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled',
      pending: 'Pending'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-9584" data-mp="2qwf3pg7mz7is">
        {labels[status] || status}
      </span>;
  };
  const handleApproval = () => {
    // In a real app, this would make an API call
    console.log(`${approvalAction} refund with notes:`, approvalNotes);
    setShowApprovalModal(false);
    // Navigate back or show success message
  };
  return <div className="space-y-6" data-id="element-9585" data-mp="1gvax1np4y574">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-9586" data-mp="1bxrb01m74o4m">
        <button onClick={() => navigate('/refunds')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-9587" data-mp="1byasjh7xpai4">
          <ArrowLeftIcon size={20} data-id="element-9588" data-mp="k6v6ppe7brsy" />
          {t('pages.RefundDetail.back_to_refunds')}
        </button>
        {refund.status === 'pending_approval' && <div className="flex gap-2" data-id="element-9589" data-mp="xztao04pms84">
            <button onClick={() => {
          setApprovalAction('reject');
          setShowApprovalModal(true);
        }} className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50" data-id="element-9590" data-mp="t6i3fxvzk9fo">
              <XCircleIcon size={18} data-id="element-9591" data-mp="20l3v6d54hxf5" />
              {t('pages.RefundDetail.reject')}
            </button>
            <button onClick={() => {
          setApprovalAction('approve');
          setShowApprovalModal(true);
        }} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-9592" data-mp="25j5g73m406t9">
              <CheckCircleIcon size={18} data-id="element-9593" data-mp="1hlzp9d8vojrp" />
              {t('pages.RefundDetail.approve')}
            </button>
          </div>}
      </div>
      {/* Refund Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9594" data-mp="1mmx71opz7bso">
        <div className="flex items-start justify-between mb-4" data-id="element-9595" data-mp="1ricgm4gyalja">
          <div data-id="element-9596" data-mp="1vm0raw0vpx8r">
            <div className="flex items-center gap-3 mb-2" data-id="element-9597" data-mp="1vmk4y0jhxzgz">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-9598" data-mp="32z2r3zsepzip">
                {refund.refundId}
              </h1>
              {refund.isPartial && <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-medium rounded" data-id="element-9599" data-mp="36z36bmnbyeqt">
                  {t('pages.RefundDetail.partial_refund')}
                </span>}
            </div>
            <div className="text-sm text-gray-500" data-id="element-9600" data-mp="rdtts3f4oyvi">
              {t('pages.RefundDetail.transaction')} {refund.transactionId}
            </div>
          </div>
          {getStatusBadge(refund.status)}
        </div>
        {/* Key Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9601" data-mp="cn32qxld23tg">
          <div data-id="element-9602" data-mp="ckgnebs8ak0o">
            <div className="text-sm text-gray-500 mb-1" data-id="element-9603" data-mp="7nzw58pqfpjd">
              {t('pages.RefundDetail.refund_amount')}
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-9604" data-mp="3n688fcaiaqh">
              {t('pages.RefundDetail.usd')}{' '}
              {refund.refundAmount.toLocaleString('en-US')}
            </div>
            {refund.isPartial && <div className="text-sm text-gray-500 mt-1" data-id="element-9605" data-mp="1xze7g4vn9ds2">
                {t('pages.RefundDetail.of_usd')}{' '}
                {refund.originalAmount.toLocaleString('en-US')}{' '}
                {t('pages.RefundDetail.original')}
              </div>}
          </div>
          <div data-id="element-9606" data-mp="16ua9rfrokrzv">
            <div className="text-sm text-gray-500 mb-1" data-id="element-9607" data-mp="16u0lbwmaaswa">
              {t('pages.RefundDetail.requested_by')}
            </div>
            <div className="text-lg font-medium text-gray-900" data-id="element-9608" data-mp="x50ghwl3p3a6">
              {refund.requestedBy}
            </div>
            <div className="text-sm text-gray-500" data-id="element-9609" data-mp="j4pogq821inf">
              {new Date(refund.requestedAt).toLocaleString('en-GB')}
            </div>
          </div>
          <div data-id="element-9610" data-mp="n4q2wav5ltuh">
            <div className="text-sm text-gray-500 mb-1" data-id="element-9611" data-mp="n4hscpmi70mg">
              {t('pages.RefundDetail.merchant')}
            </div>
            <div className="text-lg font-medium text-gray-900" data-id="element-9612" data-mp="e5mhv1cff4dc">
              {refund.merchantName}
            </div>
            <div className="text-sm text-gray-500" data-id="element-9613" data-mp="23m42unf6xgm0">{refund.merchantId}</div>
          </div>
        </div>
      </div>
      {/* Customer & Payment Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9614" data-mp="28ht5x0qf5fn2">
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9615" data-mp="3t2pvuqqzdh5x">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9616" data-mp="3o3whyx7s7o58">
            <UserIcon size={20} className="text-blue-600" data-id="element-9617" data-mp="6n52iehvcvcg" />
            {t('pages.RefundDetail.customer_information')}
          </h3>
          <div className="space-y-3" data-id="element-9618" data-mp="1uc2g3xcib6p">
            <div data-id="element-9619" data-mp="fnbg00dkhvbm">
              <div className="text-sm text-gray-500" data-id="element-9620" data-mp="fms7tbdpylas">
                {t('pages.RefundDetail.name')}
              </div>
              <div className="text-base font-medium text-gray-900" data-id="element-9621" data-mp="an6z498qyykv">
                {refund.customerName}
              </div>
            </div>
            <div data-id="element-9622" data-mp="pjep5q6u6u82">
              <div className="text-sm text-gray-500" data-id="element-9623" data-mp="kmejhgdwfthh">
                {t('pages.RefundDetail.email')}
              </div>
              <div className="text-base font-medium text-gray-900" data-id="element-9624" data-mp="2rc2npm8jcthi">
                {refund.customerEmail}
              </div>
            </div>
            <div data-id="element-9625" data-mp="2mflyeoplo92u">
              <div className="text-sm text-gray-500" data-id="element-9626" data-mp="2mg456f9w25p0">
                {t('pages.RefundDetail.payment_method')}
              </div>
              <div className="flex items-center gap-2" data-id="element-9627" data-mp="j8ts8zh77bqn">
                <CreditCardIcon size={16} className="text-gray-400" data-id="element-9628" data-mp="jbg2x7zdy7d3" />
                <span className="text-base font-medium text-gray-900" data-id="element-9629" data-mp="eb1uvpxsaxf2">
                  {refund.paymentMethod}{' '}
                  {refund.cardLast4 && `•••• ${refund.cardLast4}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9630" data-mp="11zl01wljeu2x">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9631" data-mp="x94l2822j0eg">
            <FileTextIcon size={20} className="text-blue-600" data-id="element-9632" data-mp="x6rytipa23vl" />
            {t('pages.RefundDetail.refund_reason')}
          </h3>
          <div className="space-y-3" data-id="element-9633" data-mp="1ppp3a2utcrte">
            <div data-id="element-9634" data-mp="1pqqf49jnlic6">
              <div className="text-sm text-gray-500" data-id="element-9635" data-mp="1pr9y7tl1icrs">
                {t('pages.RefundDetail.category')}
              </div>
              <div className="text-base font-medium text-gray-900 capitalize" data-id="element-9636" data-mp="1t7mkpjwz3t73">
                {refund.reason.replace(/_/g, ' ')}
              </div>
            </div>
            {refund.reasonDescription && <div data-id="element-9637" data-mp="227hxtua39y9r">
                <div className="text-sm text-gray-500" data-id="element-9638" data-mp="2279mi6tm710o">
                  {t('pages.RefundDetail.description')}
                </div>
                <div className="text-base text-gray-900" data-id="element-9639" data-mp="2751ef0u9en4v">
                  {refund.reasonDescription}
                </div>
              </div>}
            {refund.notes && <div data-id="element-9640" data-mp="2g5zmpvbqdgt9">
                <div className="text-sm text-gray-500" data-id="element-9641" data-mp="2g5g4edi64qep">
                  {t('pages.RefundDetail.additional_notes')}
                </div>
                <div className="text-base text-gray-900" data-id="element-9642" data-mp="2pxvmdatgtezl">{refund.notes}</div>
              </div>}
          </div>
        </div>
      </div>
      {/* Approval Workflow */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9643" data-mp="2zv9zb0g6jln5">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-9644" data-mp="2ztfcfglcljgf">
          {t('pages.RefundDetail.approval_workflow')}
        </h3>
        <div className="space-y-4" data-id="element-9645" data-mp="2592zjeovpzwb">
          {refund.approvalWorkflow.map((step, index) => <div key={step.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg" data-id="element-9646" data-mp="2jxz7zcuulexg" data-mpi={index}>
              <div className="flex-shrink-0" data-id="element-9647" data-mp="2f5fsgaclo9q0">
                {step.status === 'approved' ? <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-9648" data-mp="2swaub2oolqk3">
                    <CheckCircleIcon size={20} className="text-green-600" data-id="element-9649" data-mp="2nysrlu19c7kj" />
                  </div> : step.status === 'rejected' ? <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-9650" data-mp="2xwp16hejqugj">
                    <XCircleIcon size={20} className="text-red-600" data-id="element-9651" data-mp="18hio97c9gc4s" />
                  </div> : <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-9652" data-mp="13kispqd1errx">
                    <ClockIcon size={20} className="text-yellow-600" data-id="element-9653" data-mp="3f7vtk3kaypev" />
                  </div>}
              </div>
              <div className="flex-1" data-id="element-9654" data-mp="3k7qk26061cze">
                <div className="flex items-center justify-between mb-2" data-id="element-9655" data-mp="35hj8ma8ksu87">
                  <div data-id="element-9656" data-mp="3afugiffb8uns">
                    <div className="text-sm font-medium text-gray-900" data-id="element-9657" data-mp="3af2m37xg92z3">
                      {step.approverName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-9658" data-mp="2wersvy8o3car">
                      {step.approverEmail}
                    </div>
                  </div>
                  {getStatusBadge(step.status)}
                </div>
                {step.timestamp && <div className="text-sm text-gray-500 mb-2" data-id="element-9659" data-mp="2rg6tg118ownd">
                    {new Date(step.timestamp).toLocaleString('en-GB')}
                  </div>}
                {step.notes && <div className="mt-2 p-3 bg-gray-50 rounded text-sm text-gray-700" data-id="element-9660" data-mp="pokbeozbsc31">
                    {step.notes}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Lifecycle Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9661" data-mp="2crtjhciq7r1y">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-9662" data-mp="2cu6bkii77hsq">
          {t('pages.RefundDetail.refund_timeline')}
        </h3>
        <div className="space-y-4" data-id="element-9663" data-mp="22tm6t5vgzv5r">
          {refund.lifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-9664" data-mp="1y35szknqm7iv" data-mpi={index}>
              <div className="flex flex-col items-center" data-id="element-9665" data-mp="1y1mcpu22zwh3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === refund.lifecycle.length - 1 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-9666" data-mp="1t3kt980qtv53">
                  {event.status === 'completed' ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-9667" data-mp="1k5h6k8e0usbp" /> : event.status === 'rejected' || event.status === 'failed' ? <XCircleIcon size={16} className="text-red-600" data-id="element-9668" data-mp="3qr53dpwhfck2" /> : <ClockIcon size={16} className={index === refund.lifecycle.length - 1 ? 'text-blue-600' : 'text-gray-400'} data-id="element-9669" data-mp="3vpgc1xb1jh0p" />}
                </div>
                {index < refund.lifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-9670" data-mp="3d1bbeypokc64"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-9671" data-mp="3h121t1baoqa4">
                <div className="text-sm font-medium text-gray-900" data-id="element-9672" data-mp="3lsk1lm1l0kti">
                  {event.description}
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-9673" data-mp="2pbh38ugb3bs7">
                  {new Date(event.timestamp).toLocaleString('en-GB')}
                </div>
                {event.actor && <div className="text-xs text-gray-500 mt-1" data-id="element-9674" data-mp="h95l6718het">
                    {t('pages.RefundDetail.by')} {event.actor}
                  </div>}
                {event.metadata && <div className="mt-2 p-2 bg-gray-50 rounded text-xs" data-id="element-9675" data-mp="a83rg9gd9ezq">
                    {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-9676" data-mp="59amj5f5awb6">
                        <span className="font-medium" data-id="element-9677" data-mp="3do2m4z5s4r2l">
                          {key.replace(/_/g, ' ')}:
                        </span>{' '}
                        {String(value)}
                      </div>)}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Approval Modal */}
      {showApprovalModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-9678" data-mp="11y8jmuadb6bi">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-9679" data-mp="120up8odp7br3">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowApprovalModal(false)} data-id="element-9680" data-mp="15gpbz83rfvtq">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-9681" data-mp="1agk2vbnjcles"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-9682" data-mp="vjt8xhwms1wk">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-9683" data-mp="1o6x7p554rjzw">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-9684" data-mp="1ehx5zdz8uai0">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-9685" data-mp="1efjbp7ce12bi">
                  {approvalAction === 'approve' ? 'Approve Refund' : 'Reject Refund'}
                </h3>
                <div className="space-y-4" data-id="element-9686" data-mp="2bxb395mxc0z8">
                  <div data-id="element-9687" data-mp="2by2mri38y895">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9688" data-mp="2bym878s3verx">
                      {t('components.RefundModal.notes')}
                    </label>
                    <textarea rows={4} value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={approvalAction === 'approve' ? 'Add any notes about this approval...' : 'Provide a reason for rejection...'} data-id="element-9689" data-mp="728jugtks1xv" />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-9690" data-mp="36j6ujkory5c5">
                <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'}`} onClick={handleApproval} data-id="element-9691" data-mp="31jkk1j0bz371">
                  {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowApprovalModal(false)} data-id="element-9692" data-mp="3f4yg94zcj6mf">
                  {t('components.RefundModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}